import { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  Eye,
  Plus,
  Edit,
  Users,
  User,
  LayoutDashboard,
  Upload,
  Image as ImageIcon,
  Images,
  Ticket,
  Building,
  Settings,
  Key
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminStaffRota({ credentials, userRole }) {
  const [staffList, setStaffList] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Staff form state
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: "",
    username: "",
    password: "",
    phone: "",
    email: ""
  });
  
  // Shift form state
  const [showShiftForm, setShowShiftForm] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    staff_id: "",
    shift_date: "",
    start_time: "09:00",
    notes: ""
  });
  
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, id: null, name: "" });
  
  const headers = useMemo(() => {
    const auth = btoa(`${credentials.username}:${credentials.password}`);
    return { Authorization: `Basic ${auth}` };
  }, [credentials.username, credentials.password]);

  // Format date helper
  const formatDateStr = (date) => {
    if (!date) return null;
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Fetch staff and shifts
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      
      const [staffRes, shiftsRes] = await Promise.all([
        axios.get(`${API}/staff`, { headers }),
        axios.get(`${API}/shifts?month=${month}&year=${year}`, { headers })
      ]);
      
      setStaffList(staffRes.data);
      setShifts(shiftsRes.data);
    } catch (error) {
      console.error("Error fetching staff data:", error);
      toast.error("Failed to load staff data");
    } finally {
      setLoading(false);
    }
  }, [headers, currentMonth]);

  useEffect(() => {
    if (credentials.username && credentials.password) {
      fetchData();
    }
  }, [credentials.username, credentials.password, fetchData]);

  // Staff CRUD
  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    
    if (!staffForm.name || !staffForm.username) {
      toast.error("Name and username are required");
      return;
    }
    
    if (!editingStaff && !staffForm.password) {
      toast.error("Password is required for new staff");
      return;
    }

    try {
      if (editingStaff) {
        const updateData = {
          name: staffForm.name,
          phone: staffForm.phone || null,
          email: staffForm.email || null
        };
        if (staffForm.password) {
          updateData.password = staffForm.password;
        }
        await axios.patch(`${API}/staff/${editingStaff.id}`, updateData, { headers });
        toast.success("Staff member updated");
      } else {
        await axios.post(`${API}/staff`, staffForm, { headers });
        toast.success("Staff member added");
      }
      
      setShowStaffForm(false);
      setEditingStaff(null);
      setStaffForm({ name: "", username: "", password: "", phone: "", email: "" });
      fetchData();
    } catch (error) {
      console.error("Error saving staff:", error);
      const message = error.response?.data?.detail || "Failed to save staff member";
      toast.error(message);
    }
  };

  const handleEditStaff = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff.name,
      username: staff.username,
      password: "",
      phone: staff.phone || "",
      email: staff.email || ""
    });
    setShowStaffForm(true);
  };

  const handleDeleteStaff = async () => {
    if (!deleteConfirm.id) return;
    
    try {
      await axios.delete(`${API}/staff/${deleteConfirm.id}`, { headers });
      toast.success("Staff member deleted");
      setDeleteConfirm({ show: false, type: null, id: null, name: "" });
      fetchData();
    } catch (error) {
      console.error("Error deleting staff:", error);
      toast.error("Failed to delete staff member");
    }
  };

  // Shift CRUD
  const handleShiftSubmit = async (e) => {
    e.preventDefault();
    
    if (!shiftForm.staff_id || !shiftForm.shift_date || !shiftForm.start_time) {
      toast.error("Staff member, date, and start time are required");
      return;
    }

    try {
      await axios.post(`${API}/shifts`, shiftForm, { headers });
      toast.success("Shift added");
      setShowShiftForm(false);
      setShiftForm({
        staff_id: "",
        shift_date: "",
        start_time: "09:00",
        notes: ""
      });
      fetchData();
    } catch (error) {
      console.error("Error creating shift:", error);
      const message = error.response?.data?.detail || "Failed to create shift";
      toast.error(message);
    }
  };

  const handleDeleteShift = async () => {
    if (!deleteConfirm.id) return;
    
    try {
      await axios.delete(`${API}/shifts/${deleteConfirm.id}`, { headers });
      toast.success("Shift deleted");
      setDeleteConfirm({ show: false, type: null, id: null, name: "" });
      fetchData();
    } catch (error) {
      console.error("Error deleting shift:", error);
      toast.error("Failed to delete shift");
    }
  };

  // Add shift for specific date
  const handleAddShiftForDate = (date) => {
    setShiftForm(prev => ({
      ...prev,
      shift_date: formatDateStr(date)
    }));
    setShowShiftForm(true);
  };

  // Get shifts for selected date
  const selectedDateStr = formatDateStr(selectedDate);
  const dayShifts = selectedDateStr ? shifts.filter(s => s.shift_date === selectedDateStr) : [];

  // Get dates with shifts for calendar highlighting
  const shiftDates = [...new Set(shifts.map(s => s.shift_date))].map(d => new Date(d + 'T12:00:00'));

  const formatDateDisplay = (d) => d?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (userRole !== 'admin') {
    return (
      <div className="text-center py-12">
        <p className="text-[#78716C]">Admin access required</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-staff-rota">
      <h1 className="heading-lg mb-8">Staff Rota</h1>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Staff Management */}
        <div className="xl:col-span-1">
          <Card className="card-heritage">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#14532D]" />
                  Staff Members
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() => {
                    setEditingStaff(null);
                    setStaffForm({ name: "", username: "", password: "", phone: "", email: "" });
                    setShowStaffForm(true);
                  }}
                  className="btn-primary"
                  data-testid="add-staff-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-12 bg-[#E7E5E4] rounded"></div>
                  <div className="h-12 bg-[#E7E5E4] rounded"></div>
                </div>
              ) : staffList.length === 0 ? (
                <p className="text-[#78716C] text-center py-6">No staff members yet</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {staffList.map(staff => (
                    <div 
                      key={staff.id} 
                      className="p-3 border border-[#E7E5E4] rounded-lg hover:border-[#14532D] transition-colors"
                      data-testid={`staff-member-${staff.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#14532D] rounded-full flex items-center justify-center text-white font-semibold">
                            {staff.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-[#1C1917]">{staff.name}</p>
                            <p className="text-xs text-[#78716C]">@{staff.username}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditStaff(staff)}
                            data-testid={`edit-staff-${staff.id}`}
                          >
                            <Edit className="w-4 h-4 text-[#78716C]" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirm({ show: true, type: 'staff', id: staff.id, name: staff.name })}
                            data-testid={`delete-staff-${staff.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-[#B91C1C]" />
                          </Button>
                        </div>
                      </div>
                      {(staff.phone || staff.email) && (
                        <div className="mt-2 pl-13 text-xs text-[#78716C]">
                          {staff.phone && <p>{staff.phone}</p>}
                          {staff.email && <p>{staff.email}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Rota Calendar */}
        <div className="xl:col-span-2">
          <Card className="card-heritage">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#14532D]" />
                Shift Calendar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="flex justify-center">
                  <CalendarPicker
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    modifiers={{
                      hasShift: shiftDates
                    }}
                    modifiersStyles={{
                      hasShift: {
                        backgroundColor: '#14532D',
                        color: 'white',
                        borderRadius: '50%'
                      }
                    }}
                    className="rounded-md border"
                    data-testid="shift-calendar"
                  />
                </div>

                <div>
                  {selectedDate ? (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-[#1C1917]">
                          {formatDateDisplay(selectedDate)}
                        </h3>
                        <Button
                          size="sm"
                          onClick={() => handleAddShiftForDate(selectedDate)}
                          className="btn-primary"
                          data-testid="add-shift-for-date"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Shift
                        </Button>
                      </div>
                      
                      {dayShifts.length === 0 ? (
                        <p className="text-[#78716C] text-center py-6">No shifts scheduled</p>
                      ) : (
                        <div className="space-y-2">
                          {dayShifts.map(shift => (
                            <div 
                              key={shift.id}
                              className="p-3 bg-[#F5F5F0] rounded-lg border border-[#E7E5E4]"
                              data-testid={`shift-${shift.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-[#14532D]" />
                                  <span className="font-medium">{shift.start_time}</span>
                                  {shift.end_time && (
                                    <>
                                      <span className="text-[#78716C]">-</span>
                                      <span className="font-medium">{shift.end_time}</span>
                                    </>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setDeleteConfirm({ show: true, type: 'shift', id: shift.id, name: `${shift.staff_name}'s shift` })}
                                  data-testid={`delete-shift-${shift.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-[#B91C1C]" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <User className="w-4 h-4 text-[#78716C]" />
                                <span className="text-sm font-medium text-[#1C1917]">{shift.staff_name}</span>
                              </div>
                              {shift.notes && (
                                <p className="text-xs text-[#78716C] mt-2 pl-6">{shift.notes}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <CalendarDays className="w-12 h-12 text-[#D6D3D1] mx-auto mb-3" />
                      <p className="text-[#78716C]">Select a date to view or add shifts</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Shifts Summary */}
          <Card className="card-heritage mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#14532D]" />
                Shifts This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              {shifts.length === 0 ? (
                <p className="text-[#78716C] text-center py-4">No shifts scheduled this month</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E7E5E4]">
                        <th className="text-left py-2 px-3 font-semibold text-[#78716C]">Date</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#78716C]">Staff</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#78716C]">Time</th>
                        <th className="text-left py-2 px-3 font-semibold text-[#78716C]">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shifts.slice(0, 10).map(shift => (
                        <tr key={shift.id} className="border-b border-[#E7E5E4] hover:bg-[#F5F5F0]">
                          <td className="py-2 px-3">
                            {new Date(shift.shift_date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </td>
                          <td className="py-2 px-3 font-medium">{shift.staff_name}</td>
                          <td className="py-2 px-3">
                            {shift.start_time}{shift.end_time ? ` - ${shift.end_time}` : ''}
                          </td>
                          <td className="py-2 px-3 text-[#78716C] truncate max-w-[150px]">{shift.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {shifts.length > 10 && (
                    <p className="text-xs text-[#78716C] text-center mt-3">
                      Showing 10 of {shifts.length} shifts. Use the calendar to view more.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Staff Form Dialog */}
      <Dialog open={showStaffForm} onOpenChange={setShowStaffForm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingStaff ? "Edit Staff Member" : "Add Staff Member"}</DialogTitle>
            <DialogDescription>
              {editingStaff 
                ? "Update staff member details. Leave password blank to keep current." 
                : "Add a new staff member with login credentials."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStaffSubmit} className="space-y-4">
            <div>
              <Label htmlFor="staff-name" className="label-heritage">Full Name *</Label>
              <Input
                id="staff-name"
                value={staffForm.name}
                onChange={(e) => setStaffForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                className="input-heritage"
                data-testid="staff-form-name"
              />
            </div>
            <div>
              <Label htmlFor="staff-username" className="label-heritage">Username *</Label>
              <Input
                id="staff-username"
                value={staffForm.username}
                onChange={(e) => setStaffForm(prev => ({ ...prev, username: e.target.value }))}
                placeholder="jsmith"
                className="input-heritage"
                disabled={!!editingStaff}
                data-testid="staff-form-username"
              />
              {editingStaff && (
                <p className="text-xs text-[#78716C] mt-1">Username cannot be changed</p>
              )}
            </div>
            <div>
              <Label htmlFor="staff-password" className="label-heritage">
                Password {editingStaff ? "(leave blank to keep current)" : "*"}
              </Label>
              <Input
                id="staff-password"
                type="password"
                value={staffForm.password}
                onChange={(e) => setStaffForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                className="input-heritage"
                data-testid="staff-form-password"
              />
            </div>
            <div>
              <Label htmlFor="staff-phone" className="label-heritage">Phone</Label>
              <Input
                id="staff-phone"
                value={staffForm.phone}
                onChange={(e) => setStaffForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="07700 900000"
                className="input-heritage"
                data-testid="staff-form-phone"
              />
            </div>
            <div>
              <Label htmlFor="staff-email" className="label-heritage">Email</Label>
              <Input
                id="staff-email"
                type="email"
                value={staffForm.email}
                onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="input-heritage"
                data-testid="staff-form-email"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowStaffForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="staff-form-submit">
                {editingStaff ? "Update" : "Add Staff"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Shift Form Dialog */}
      <Dialog open={showShiftForm} onOpenChange={setShowShiftForm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Shift</DialogTitle>
            <DialogDescription>
              Schedule a shift for a staff member.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleShiftSubmit} className="space-y-4">
            <div>
              <Label htmlFor="shift-staff" className="label-heritage">Staff Member *</Label>
              <select
                id="shift-staff"
                value={shiftForm.staff_id}
                onChange={(e) => setShiftForm(prev => ({ ...prev, staff_id: e.target.value }))}
                className="w-full p-2 border border-[#D6D3D1] rounded-md focus:outline-none focus:ring-2 focus:ring-[#14532D]"
                data-testid="shift-form-staff"
              >
                <option value="">Select staff member</option>
                {staffList.map(staff => (
                  <option key={staff.id} value={staff.id}>{staff.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="shift-date" className="label-heritage">Date *</Label>
              <Input
                id="shift-date"
                type="date"
                value={shiftForm.shift_date}
                onChange={(e) => setShiftForm(prev => ({ ...prev, shift_date: e.target.value }))}
                className="input-heritage"
                data-testid="shift-form-date"
              />
            </div>
            <div>
              <Label htmlFor="shift-start" className="label-heritage">Start Time *</Label>
              <Input
                id="shift-start"
                type="time"
                value={shiftForm.start_time}
                onChange={(e) => setShiftForm(prev => ({ ...prev, start_time: e.target.value }))}
                className="input-heritage"
                data-testid="shift-form-start"
              />
            </div>
            <div>
              <Label htmlFor="shift-notes" className="label-heritage">Notes</Label>
              <Textarea
                id="shift-notes"
                value={shiftForm.notes}
                onChange={(e) => setShiftForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any special instructions..."
                className="input-heritage"
                rows={2}
                data-testid="shift-form-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowShiftForm(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="shift-form-submit">
                Add Shift
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, type: null, id: null, name: "" })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm.type === 'staff' 
                ? `This will permanently delete ${deleteConfirm.name} and all their scheduled shifts.`
                : `This will delete ${deleteConfirm.name}.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteConfirm.type === 'staff' ? handleDeleteStaff : handleDeleteShift}
              className="bg-[#B91C1C] hover:bg-[#991B1B]"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


// Staff My Shifts Component - for staff members to view their own shifts
